import fetch, {Response} from "node-fetch";
import {BookingService} from "../..";
import type {IBookingService} from "../../service/service.types";
import {cache} from "../helpers/cacheDecorator";
import type {
  IRepository,
  IPartnerAdsBookingSaleSubmission,
  IPartnerAdsCompanyLeadSubmission,
  IBookableSchedulesDTO,
  IBasisTypeOffered,
  IServiceDTO,
  IAllPartnerResponseDTO,
} from "../interfaces";
import {defaultMappers} from "../../mapper/mapper.types";
import {PartnerMapper} from "../../mapper/PartnerMapper";
import type {Address, BookableSchedulesDTO, BookingDataProps, DeskBookingsDTO, DeskPricesDTO, FinalizeProductDTO, FinalizeResponse, Goal, IAdminEditBookingDTO, IBookingReasonDTO, ICreateCompanyLeadDTO, IGeneralBFLead, IGetPaymentInfo, IMoveBookingDTO, IPaymentPrice, IPreviousBooking, IPrivateBookingPaymentsDTO, IThirdPartyGiftCardType, ModifyBookPartnerDetails, NumberOfServiceReviews, PackageTemplate, Partner, RRDate, Service} from "@raskrask-dk/raskrask-utils";
import {mapGetBookings} from "../../mapper/bookingMapper";
import {mapGetPricesByProfileId} from "../../mapper/priceMapper";

export interface Models {
  partners: Partner[];
  services: Service[];
  goals: Goal[];
  packages: PackageTemplate[];
}
export class Repository implements IRepository {
  private HOST: string;
  private BASE_URL: string;
  public readonly service: IBookingService;

  constructor(host: string, baseUrl: string, service?: IBookingService) {
    this.HOST = host;
    this.BASE_URL = `${this.HOST}${baseUrl}`;
    this.service = service ?? new BookingService(this);
  }

  @cache({minute: 1}) // When the computers clock changes minute, the cache will always be invalidated, as the input has changed, and thus the cache can ever maximum be 1 minute.
  async getSchedules(bookBookableSchedulesDTO: BookableSchedulesDTO, _cacheInvalid: number): Promise<IBookableSchedulesDTO> {
    const endpoint = "/schedules";
    const response: Response = await fetch(`${this.BASE_URL}${endpoint}`, {
      method: "POST",
      body: JSON.stringify(bookBookableSchedulesDTO),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status !== 200) throw new Error("Error when fetching booking");
    return await response.json();
  }

  @cache({minute: 10})
  async getOfferedBasisTypes(address: Address, startDate: string, numberOfDaysToCheck: number): Promise<IBasisTypeOffered[]> {
    const endpoint = "/offeredbasistypes";
    const body = {
      address: {
        geoLat: address.geoLat,
        geoLng: address.geoLng,
        address: address.address,
        zipCode: address.zipCode,
        floor: address.floor,
      },
      period: {
        start: startDate,
        days: numberOfDaysToCheck,
      },
    };
    const response: Response = await fetch(`${this.BASE_URL}${endpoint}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status !== 200) throw new Error("Error when fetching booking");
    return await response.json();
  }

  @cache({second: 60})
  public async getNumberOfServiceReviews(): Promise<NumberOfServiceReviews> {
    const response: Response = await fetch(`${this.BASE_URL}/nrofservicereviews`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  }

  @cache({second: 60})
  public async getBookingData(bookingId: string): Promise<IPreviousBooking> {
    const response: Response = await fetch(`${this.BASE_URL}/bookingdata`, {
      method: "POST",
      body: JSON.stringify({bookingId: bookingId}),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status !== 200) throw new Error("Error when fetching booking");
    return await response.json();
  }

  public getThirdPartyGiftCardTypes = async (): Promise<IThirdPartyGiftCardType[]> => {
    const response: Response = await fetch(`${this.BASE_URL}/thirdpartygiftcardtypes`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  };

  @cache({second: 60})
  public async getPaymentInfo(paymentInfo: IGetPaymentInfo): Promise<IPaymentPrice> {
    const response: Response = await fetch(`${this.HOST}/v1/payment/price`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({...paymentInfo}),
    });
    return await response.json();
  }

  public async finalizeProduct(product: FinalizeProductDTO, accessToken?: string): Promise<FinalizeResponse> {
    const response = await fetch(`${this.HOST}/v1/finalizeproduct`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(product),
    });
    return await response.json();
  }

  public getBookingsByPartnerIdAndPeriod = async (profileId: number, serviceFrom: RRDate, serviceTo: RRDate): Promise<BookingDataProps[]> => {
    const response: Response = await fetch(
      `${this.HOST}/v1/desk/partner/${profileId}/bookings?serviceFrom=${serviceFrom.toString("yyyy-MM-dd")}&serviceTo=${serviceTo.toString(
        "yyyy-MM-dd"
      )}`,
      {
        method: "GET",
        headers: {"Content-Type": "application/json"},
      }
    );
    const bookingsData = (await response.json()) as DeskBookingsDTO[];
    const partnerBookings = mapGetBookings(bookingsData);
    return partnerBookings;
  };

  public getPricesByProfileId = async (filters: {profileId: number}): Promise<DeskPricesDTO[]> => {
    const response: Response = await fetch(`${this.HOST}/v1/desk/partner/${filters.profileId}/prices`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const pricesData = (await response.json()) as DeskPricesDTO[];
    return mapGetPricesByProfileId(pricesData);
  };

  public async removeReepayPaymentMethodTokenIfAny(customerFirstName: string, customerEmail: string): Promise<{message: "Success"}> {
    const response: Response = await fetch(`${this.HOST}/v1/paymentmethodtoken/remove`, {
      method: "POST",
      body: JSON.stringify({
        customerFirstName,
        customerEmail,
      }),
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  }
  public async saveReepayPaymentMethodToken(
    customerFirstName: string,
    customerEmail: string,
    paymentMethodToken: string
  ): Promise<{message: "Success"}> {
    const response: Response = await fetch(`${this.HOST}/v1/paymentmethodtoken/save`, {
      method: "POST",
      body: JSON.stringify({
        customerFirstName,
        customerEmail,
        paymentMethodToken,
      }),
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  }

  public async getAllServices(): Promise<Service[]> {
    const response: Response = await fetch(`${this.HOST}/v1/bookingflow/allservices`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const serviceDTO: IServiceDTO[] = await response.json();
    return serviceDTO.map(defaultMappers.service);
  }

  public async getAllPartners(geoLat?: string, geoLng?: string): Promise<Partner[]> {
    let url = `${this.HOST}/v1/bookingflow/allpartners`;
    if (geoLat && geoLng) url += `?geoLat=${geoLat}&geoLng=${geoLng}`;
    const response: Response = await fetch(url, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const partnerDTO: IAllPartnerResponseDTO[] = await response.json();
    return partnerDTO.map((p) => {
      const mapper = new PartnerMapper(defaultMappers.partner);
      return mapper.fromAllPartnerResponseDTO(p);
    });
  }

  public async submitPartnerAdsSale(input: IPartnerAdsBookingSaleSubmission): Promise<string> {
    const body = {
      programId: 8197,
      paid: input.paid,
      pacid: input.pacid,
      ordernummer: input.ordernummer,
      omsaetningPRSalg: input.omsaetningPRSalg,
    };
    const response: Response = await fetch(`${this.HOST}/v1/bookingflow/partnerads/sale`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  }

  public async submitPartnerAdsLead(input: IPartnerAdsCompanyLeadSubmission): Promise<string> {
    const body = {
      programId: 8197,
      paid: input.paid,
      pacid: input.pacid,
      ordernummer: input.ordernummer,
    };
    const response: Response = await fetch(`${this.HOST}/v1/bookingflow/partnerads/lead`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {"Content-Type": "application/json"},
    });
    return await response.json();
  }

  public async createCompanyLead(lead: ICreateCompanyLeadDTO): Promise<{status: number}> {
    const response: Response = await fetch(`${this.HOST}/v1/bookingflow/companylead`, {
      method: "POST",
      body: JSON.stringify(lead),
      headers: {"Content-Type": "application/json"},
    });

    return {status: response.status};
  }
  public async createGeneralLead(lead: IGeneralBFLead): Promise<{status: number}> {
    const response: Response = await fetch(`${this.HOST}/v1/bookingflow/generallead`, {
      method: "POST",
      body: JSON.stringify(lead),
      headers: {"Content-Type": "application/json"},
    });
    return {status: response.status};
  }

  @cache({second: 60})
  public async getBookingReasons(): Promise<IBookingReasonDTO[]> {
    const data: Response = await fetch(`${this.HOST}/v1/bookingflow/bookingreasons`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const bookingReasons = await data.json();
    return bookingReasons;
  }

  public moveBooking = async (moveBookingObj: IMoveBookingDTO): Promise<"Success"> => {
    const response = await fetch(`${this.HOST}/v1/movebooking`, {
      method: "POST",
      body: JSON.stringify(moveBookingObj),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status === 200) return "Success";
    throw new Error("Something went wrong during move booking");
  };

  public adminEditBooking = async (moveBookingObj: IAdminEditBookingDTO, accessToken?: string): Promise<"Success"> => {
    const response = await fetch(`${this.HOST}/v1/admineditbooking`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(moveBookingObj),
    });
    if (response.status === 200) return "Success";
    throw new Error("Something went wrong during move booking");
  };

  public getLastKlippeCardCodeByPurchaserEmail = async (email: string): Promise<string | undefined> => {
    const data: Response = await fetch(`${this.HOST}/v1/bookingflow/lastklippekortofpurchaser?email=${email}`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const response = await data.json();
    const code = response.code;
    return code;
  };

  public cancelBookingCustomer = async (bookingId: string): Promise<"Success"> => {
    const response = await fetch(`${this.HOST}/v1/cancelbooking/customer`, {
      method: "POST",
      body: JSON.stringify({
        bookingId: bookingId,
      }),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status === 200) return "Success";
    throw new Error("Something went wrong during move booking");
  };

  public cancelBookingAdmin = async (
    bookingId: string,
    isCancellationFee: boolean,
    cancellationFeeSize: number,
    shouldSendPartnerMessages: boolean,
    shouldSendCustomerMessages: boolean,
    shouldCloseTime: boolean,
    cancelOnBehalfOf: string,
    cancelReason: string
  ): Promise<"Success"> => {
    const response = await fetch(`${this.HOST}/v1/cancelbooking/admin`, {
      method: "POST",
      body: JSON.stringify({
        bookingId: bookingId,
        cancellationFee: isCancellationFee,
        cancellationFeeSize: cancellationFeeSize,
        shouldSendPartnerMessages: shouldSendPartnerMessages,
        shouldSendCustomerMessages: shouldSendCustomerMessages,
        shouldCloseTime: shouldCloseTime,
        cancelOnBehalfOf: cancelOnBehalfOf,
        cancelReason: cancelReason,
      }),
      headers: {"Content-Type": "application/json"},
    });
    if (response.status === 200) return "Success";
    throw new Error("Something went wrong during move booking");
  };

  public getModifyBookPartnerDetails = async (bookingId: string): Promise<ModifyBookPartnerDetails> => {
    const response = await fetch(`${this.HOST}/v1/bookingflow/modifybookpartnerdetails?bookingId=${bookingId}`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const result = await response.json();
    return result;
  };

  public getInfoAboutPaidAmount = async (bookingId: string): Promise<IPrivateBookingPaymentsDTO> => {
    const response = await fetch(`${this.HOST}/v1/payment/paymentinfoprivatebooking?bookingId=${bookingId}`, {
      method: "GET",
      headers: {"Content-Type": "application/json"},
    });
    const result = await response.json();
    return result;
  };
}
